// Copyright (c) 2026 BlueRock Security, Inc.
// All rights reserved.
//
// BLUEROCK SECURITY, INC. PROPRIETARY AND CONFIDENTIAL INFORMATION

import type { CSSProperties, ReactNode } from 'react';
import { Card, Flex, Popover, Statistic, Typography, theme } from 'antd';
import { ArrowDown, ArrowUp, Info } from 'lucide-react';
import { MiniSparkline } from './MiniSparkline';

const { Text } = Typography;

export type KpiTone = 'success' | 'warning' | 'danger';

export interface KpiCardDelta {
  /** Percentage-point (or %) change, sign implies direction unless `direction` is set. */
  value: number;
  direction?: 'up' | 'down';
  /**
   * When true, an upward change reads as bad (colorError) and downward
   * as good (colorSuccess) — e.g. cost, waste. Defaults to false (up = good).
   */
  invert?: boolean;
}

export interface KpiCardSecondary {
  label: string;
  value: ReactNode;
}

export interface KpiCardProps {
  title: ReactNode;
  /** Fully preformatted by the caller (e.g. `loading ? undefined : formatUsd(x)`); renders '—' when omitted. */
  value?: string | number;
  /** Passed straight through to Statistic's `prefix` slot. */
  icon?: ReactNode;
  /** Passed straight through to Statistic's `suffix` slot (e.g. "last 24h"). */
  suffix?: ReactNode;
  /** Colors the value text — success/warning/danger. No background fill. */
  tone?: KpiTone;
  /** Trend indicator rendered under the value. */
  delta?: KpiCardDelta;
  /** Trend line rendered under the value. */
  sparkline?: number[];
  /** Small sub-metric line under the main value. */
  secondary?: KpiCardSecondary;
  /** Info icon + popover next to the title, for methodology notes or caveats. */
  tooltip?: ReactNode;
  /** Warning-toned caveat line rendered below everything else (e.g. "data incomplete"). */
  footnote?: ReactNode;
  onClick?: () => void;
  /** Only meaningful with `onClick` — shows a colorPrimary border ring, never a fill. */
  active?: boolean;
  style?: CSSProperties;
  className?: string;
}

/**
 * Shared KPI/stat tile. One component composed via optional slots rather
 * than named variants — every consumer is title+value plus zero or more
 * of icon/suffix/tone/delta/sparkline/secondary/onClick.
 *
 * Deliberately excludes: background-tinted cards, and any hover/scroll
 * expand animation. Loading is the caller's responsibility (pass
 * `value={undefined}` for the baseline '—' placeholder); this component
 * has no opinion on retry/error states.
 */
export function KpiCard({
  title,
  value,
  icon,
  suffix,
  tone,
  delta,
  sparkline,
  secondary,
  tooltip,
  footnote,
  onClick,
  active,
  style,
  className,
}: KpiCardProps) {
  const { token } = theme.useToken();

  const toneColor =
    tone === 'danger' ? token.colorError : tone === 'warning' ? token.colorWarning : tone === 'success' ? token.colorSuccess : undefined;

  const titleNode = tooltip ? (
    <Flex align="center" gap={4}>
      <span>{title}</span>
      <Popover content={tooltip}>
        <Info size={14} strokeWidth={1.75} color={token.colorTextTertiary} className="cursor-help" />
      </Popover>
    </Flex>
  ) : (
    title
  );

  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        flex: 1,
        minWidth: 200,
        ...(active
          ? { borderColor: token.colorPrimary, boxShadow: `0 0 0 1px ${token.colorPrimary}` }
          : {}),
        ...style,
      }}
      styles={{ body: { padding: `${token.paddingSM}px ${token.padding}px` } }}
      className={className}
    >
      <Statistic
        title={titleNode}
        value={value ?? '—'}
        prefix={icon}
        suffix={suffix}
        styles={{
          // hyphens:auto has no effect here — Chrome doesn't consult the
          // hyphenation dictionary once overflow-wrap:anywhere is set (it
          // wins with a forced, unhyphenated break), and removing anywhere
          // reopens the original overflow bug this was fixing (verified:
          // "Reviewed/Dismissed Alerts" spills past its card without it).
          // Keeping anywhere as the guaranteed no-overflow fix.
          title: { overflowWrap: 'anywhere' },
          content: { fontSize: 22, fontWeight: 600, color: toneColor },
          suffix: { fontSize: token.fontSizeSM, fontWeight: 400, color: token.colorTextSecondary },
        }}
      />
      {(delta || sparkline) && (
        <Flex align="center" justify="space-between" gap={8} style={{ marginTop: token.marginXS }}>
          {delta && <DeltaIndicator delta={delta} />}
          {sparkline && <MiniSparkline points={sparkline} />}
        </Flex>
      )}
      {secondary && (
        <Flex align="center" gap={4} style={{ marginTop: token.marginXS }}>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {secondary.label}
          </Text>
          <Text style={{ fontSize: token.fontSizeSM }}>{secondary.value}</Text>
        </Flex>
      )}
      {footnote && (
        <div style={{ marginTop: token.marginXS }}>
          <Text type="warning" style={{ fontSize: token.fontSizeSM, lineHeight: 1.3 }}>
            {footnote}
          </Text>
        </div>
      )}
    </Card>
  );
}

function DeltaIndicator({ delta }: { delta: KpiCardDelta }) {
  const { token } = theme.useToken();
  const direction = delta.direction ?? (delta.value >= 0 ? 'up' : 'down');
  const isGood = delta.invert ? direction === 'down' : direction === 'up';
  const color = isGood ? token.colorSuccess : token.colorError;
  const Arrow = direction === 'up' ? ArrowUp : ArrowDown;
  return (
    <Flex align="center" gap={2}>
      <Arrow size={11} color={color} />
      <Text style={{ fontSize: token.fontSizeSM, color }} className="tabular-nums">
        {Math.abs(delta.value).toFixed(1)}%
      </Text>
    </Flex>
  );
}

export default KpiCard;
